import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';

const QuoteForm = ({ onSubmit, onCancel, loading = false, leadId = null }) => {
  const { t } = useTranslation();
  
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors }
  } = useForm({
    defaultValues: {
      lead_id: leadId || '',
      product_type: '',
      annual_premium: '',
      deductible: '',
      coverage_amount: '',
      // Auto details
      auto_year: '',
      auto_make: '',
      auto_model: '',
      auto_vin: '',
      auto_driver_age: '',
      auto_driver_experience: '',
      auto_annual_mileage: '',
      // Casa details
      casa_property_value: '',
      casa_property_age: '',
      casa_square_footage: '',
      casa_roof_type: '',
      casa_foundation_type: '',
      // Comercial details
      comercial_business_type: '',
      comercial_annual_revenue: '',
      comercial_employee_count: '',
      comercial_industry_classification: '',
      comercial_liability_needs: '',
      // General
      notes: ''
    }
  });

  const PRODUCT_TYPES = ['Auto', 'Casa', 'Comercial'];

  const onFormSubmit = (data) => {
    // Transform data to match backend structure
    const transformedData = {
      lead_id: data.lead_id,
      product_type: data.product_type,
      annual_premium: parseFloat(data.annual_premium) || 0,
      deductible: parseFloat(data.deductible) || 0,
      coverage_amount: parseFloat(data.coverage_amount) || 0,
      auto_details: {
        year: parseInt(data.auto_year) || 0,
        make: data.auto_make,
        model: data.auto_model,
        vin: data.auto_vin,
        driver_age: parseInt(data.auto_driver_age) || 0,
        driver_experience: parseInt(data.auto_driver_experience) || 0,
        annual_mileage: parseInt(data.auto_annual_mileage) || 0
      },
      casa_details: {
        property_value: parseFloat(data.casa_property_value) || 0,
        property_age: parseInt(data.casa_property_age) || 0,
        square_footage: parseInt(data.casa_square_footage) || 0,
        roof_type: data.casa_roof_type,
        foundation_type: data.casa_foundation_type
      },
      comercial_details: {
        business_type: data.comercial_business_type,
        annual_revenue: parseFloat(data.comercial_annual_revenue) || 0,
        employee_count: parseInt(data.comercial_employee_count) || 0,
        industry_classification: data.comercial_industry_classification,
        liability_needs: data.comercial_liability_needs
      },
      notes: data.notes
    };
    onSubmit(transformedData);
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
      {/* Basic Quote Info */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="product_type">{t('quotes.productType')}*</Label>
          <Select
            value={watch('product_type')}
            onValueChange={(value) => setValue('product_type', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder={t('quotes.productType')} />
            </SelectTrigger>
            <SelectContent>
              {PRODUCT_TYPES.map((type) => (
                <SelectItem key={type} value={type}>
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!watch('product_type') && (
            <span className="text-sm text-red-500">Product type is required</span>
          )}
        </div>
        <div>
          <Label htmlFor="annual_premium">{t('quotes.annualPremium')}*</Label>
          <input
            {...register('annual_premium', { required: 'Annual premium is required' })}
            id="annual_premium"
            type="number"
            step="0.01"
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            placeholder={t('quotes.annualPremium')}
          />
          {errors.annual_premium && (
            <span className="text-sm text-red-500">{errors.annual_premium.message}</span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="deductible">{t('quotes.deductible')}</Label>
          <input
            {...register('deductible')}
            id="deductible"
            type="number"
            step="0.01"
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            placeholder={t('quotes.deductible')}
          />
        </div>
        <div>
          <Label htmlFor="coverage_amount">{t('quotes.coverageAmount')}</Label>
          <input
            {...register('coverage_amount')}
            id="coverage_amount"
            type="number"
            step="0.01"
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            placeholder={t('quotes.coverageAmount')}
          />
        </div>
      </div>

      {/* Product-specific details based on selected type */}
      <Tabs defaultValue="auto" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="auto">Auto Details</TabsTrigger>
          <TabsTrigger value="casa">Casa Details</TabsTrigger>
          <TabsTrigger value="comercial">Comercial Details</TabsTrigger>
        </TabsList>
        
        <TabsContent value="auto" className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="auto_year">Year</Label>
              <input
                {...register('auto_year')}
                id="auto_year"
                type="number"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="Year"
              />
            </div>
            <div>
              <Label htmlFor="auto_make">Make</Label>
              <input
                {...register('auto_make')}
                id="auto_make"
                type="text"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="Make"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="auto_model">Model</Label>
              <input
                {...register('auto_model')}
                id="auto_model"
                type="text"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="Model"
              />
            </div>
            <div>
              <Label htmlFor="auto_vin">VIN</Label>
              <input
                {...register('auto_vin')}
                id="auto_vin"
                type="text"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="VIN"
              />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="casa" className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="casa_property_value">Property Value</Label>
              <input
                {...register('casa_property_value')}
                id="casa_property_value"
                type="number"
                step="0.01"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="Property Value"
              />
            </div>
            <div>
              <Label htmlFor="casa_property_age">Property Age</Label>
              <input
                {...register('casa_property_age')}
                id="casa_property_age"
                type="number"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="Property Age"
              />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="comercial" className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="comercial_business_type">Business Type</Label>
              <input
                {...register('comercial_business_type')}
                id="comercial_business_type"
                type="text"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="Business Type"
              />
            </div>
            <div>
              <Label htmlFor="comercial_annual_revenue">Annual Revenue</Label>
              <input
                {...register('comercial_annual_revenue')}
                id="comercial_annual_revenue"
                type="number"
                step="0.01"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="Annual Revenue"
              />
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <div>
        <Label htmlFor="notes">{t('quotes.notes')}</Label>
        <Textarea
          {...register('notes')}
          id="notes"
          rows={4}
          className="resize-none"
          placeholder={t('quotes.notes')}
        />
      </div>

      <div className="flex justify-end space-x-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={loading || !watch('product_type')} className="bg-mg-green hover:bg-mg-green/90">
          {loading ? t('quotes.creating') : t('quotes.createQuote')}
        </Button>
      </div>
    </form>
  );
};

export default QuoteForm;